import { useContext } from 'react';
import { db, storage } from './firebase';
import { collection, addDoc, getDocs, arrayUnion, setDoc, Timestamp, updateDoc, increment, doc, getDoc } from 'firebase/firestore';
import { openDB } from 'idb';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Centralized logging
const logger = {
  info: (...args) => console.log('[INFO]', ...args),
  error: (...args) => console.error('[ERROR]', ...args)
};

/**
 * Uploads order images to Firebase Storage
 * @param {Array} images - Array of image blobs
 * @param {string} shopId - Shop identifier
 * @param {string} orderId - Order identifier
 * @returns {Promise<string[]>} Array of image URLs
 */
const uploadOrderImages = async (images, shopId, orderId) => {
  const uploadedImages = [];
  for (const img of images) {
    const storageRef = ref(storage, `orders/${shopId}/${orderId}/${img.name}`);
    await uploadBytes(storageRef, img);
    const imageUrl = await getDownloadURL(storageRef);
    uploadedImages.push(imageUrl);
  }
  return uploadedImages;
};

/**
 * Validates order details
 * @param {Object} orderDetails - Order information
 * @throws {Error} If validation fails
 */
const validateOrderDetails = (orderDetails) => {
  if (!orderDetails.shopId) throw new Error('Shop ID is required');
  if (!orderDetails.orderID) throw new Error('Order ID is required');
  if (!orderDetails.phoneNumber) throw new Error('Phone number is required');
};

// Initialize IndexedDB
const initializeDB = async () => {
  return await openDB('OrdersDB', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('orders')) {
        const store = db.createObjectStore('orders', { keyPath: 'localId', autoIncrement: true });
        store.createIndex('syncStatus', 'syncStatus');
      }
      if (!db.objectStoreNames.contains('pendingOperations')) {
        db.createObjectStore('pendingOperations', { keyPath: 'timestamp' });
      }
      if (!db.objectStoreNames.contains('cashbook')) {
        const store = db.createObjectStore('cashbook', { keyPath: 'localId', autoIncrement: true });
        store.createIndex('syncStatus', 'syncStatus');
      }
    }
  });
};

/**
 * Saves an order with offline support
 * @param {Object} orderDetails - Order information
 * @param {Array} orderImages - Array of image blobs
 * @param {string} shopId - Shop identifier
 * @param {string} userId - User identifier
 * @param {string} userName - User name
 * @returns {Promise<Object>} Save result
 */
const saveOrder = async (orderDetails, orderImages, shopId, userId, userName) => {
  validateOrderDetails(orderDetails);
  
  const cashbookEntries = orderDetails.payments
    .filter(payment => payment.amount > 0)
    .map(payment => ({
      amount: Number(payment.amount),
      paymentMethod: payment.method,
      timestamp: Timestamp.now(),
      shopId,
      orderId: Number(orderDetails.orderID),
      description: `Payment of Order #${orderDetails.orderID} via ${payment.method}`,
      transactionType: 'income',
      userId,
      userName,
      syncStatus: 'pending'
    }));

  if (navigator.onLine) {
      // Process and upload multiple blob images
      const imageUrls = await uploadOrderImages(orderImages, shopId, orderDetails.orderID);
      orderDetails.imageUrls = imageUrls;
      const ordImgs = orderDetails.orderImages;
      delete orderDetails.orderImages;

      //Save Order Data
      const docRef = await addDoc(collection(db, 'orders'), orderDetails);
      console.log("Save Document to ",docRef.id);
      orderDetails.orderImages = ordImgs;

      //Save Cashbook Data
      for (const entry of cashbookEntries) {
        const cashbookRef = await addDoc(collection(db, 'cashbook'), entry);
        console.log("Cashbook Document to ",cashbookRef.id);
      }
      //Check Customer and Save Data
      const customerRef = doc(collection(db, 'customers'), orderDetails.phoneNumber);
      const customerSnap = await getDoc(customerRef);

      if (customerSnap.exists()) {
        // Update existing customer
        await updateDoc(customerRef, {
        orderIds: arrayUnion(orderDetails.orderID), 
      });
      } 
      else {
        await setDoc(customerRef, {
          customerName: orderDetails.customerName,
          phoneNumber: orderDetails.phoneNumber,
          orderIds: [orderDetails.orderID], 
          createDate: Timestamp.now(),
          shopId: shopId
        });
      }
      //Deduct credit
      const shopRef = doc(db, "shops", shopId);
      const shopDoc = await getDoc(shopRef);
      if (shopDoc.exists()) {
        await updateDoc(shopRef, {
         credit: increment(-1) // Deduct 1 credit
      });
      }

      //Save Order Data to IndexedDB
      const idb = await initializeDB();
      const tx = idb.transaction(['orders', 'pendingOperations'], 'readwrite');
      await tx.objectStore('orders').add({
        ...orderDetails,
        firebaseId: docRef.id,
        syncStatus: 'synced'
      });
      await tx.done;
 

    return { success: true, id: docRef.id };

  } else {
    const idb = await initializeDB();
    const tx = idb.transaction(['orders', 'pendingOperations', 'cashbook'], 'readwrite');
    try {
      const localId = await tx.objectStore('orders').add({
        ...orderDetails,
        syncStatus: 'pending'
      });
      
      await tx.objectStore('pendingOperations').add({
        type: 'add',
        data: orderDetails,
        timestamp: Date.now(),
        localId
      });

      // Save cashbook entries locally
      for (const entry of cashbookEntries) {
        await tx.objectStore('cashbook').add(entry);
      }

      await tx.done;
      return { success: true, id: localId, offline: true };
    } catch (error) {
      logger.error('Error saving offline order:', error);
      throw error;
    }
  }
};

/**
 * Syncs pending orders and cashbook entries
 */
const syncPendingOrders = async () => {
  const idb = await initializeDB();
  const pendingOps = await idb.getAll('pendingOperations');
  const pendingCashbook = await idb.getAllFromIndex('cashbook', 'syncStatus', 'pending');

  // Sync orders
  for (const op of pendingOps) {
    try {
      if (op.type === 'add') {
        const imageUrls = await uploadOrderImages(op.data.orderImages, op.data.shopId, op.data.orderID);
        op.data.imageUrls = imageUrls;
        const ordImgs = op.data.orderImages;
        delete op.data.orderImages;

        const docRef = await addDoc(collection(db, 'orders'), op.data);
        op.data.orderImages = ordImgs;

        const tx = idb.transaction(['orders', 'pendingOperations'], 'readwrite');
        const localOrder = await tx.objectStore('orders').get(op.localId);
        await tx.objectStore('orders').put({
          ...localOrder,
          firebaseId: docRef.id,
          syncStatus: 'synced'
        });
        await tx.objectStore('pendingOperations').delete(op.timestamp);
        await tx.done;
      }
    } catch (error) {
      logger.error('Error syncing operation:', op, error);
    }
  }

  // Sync cashbook entries
  for (const entry of pendingCashbook) {
    try {
      console.log("Entry Timestamp is",entry.timestamp);
      const firebaseTimestamp = entry.timestamp;
      const date = new Date(firebaseTimestamp.seconds * 1000 + firebaseTimestamp.nanoseconds / 1000000);
      const formattedEntry = {
        ...entry,
        syncStatus: 'synced',
        timestamp: date // Or your preferred format
      };

      // First, perform the Firebase operation with formatted entry
      const cashbookRef = await addDoc(collection(db, 'cashbook'), formattedEntry);
      
      // Then start the IDB transaction and complete it without interruption
      const tx = idb.transaction('cashbook', 'readwrite');
      await tx.objectStore('cashbook').put({
        ...entry,
        firebaseId: cashbookRef.id,
        syncStatus: 'synced'
      });
      await tx.done;
    } catch (error) {
      logger.error('Error syncing cashbook entry:', entry, error);
    }
  }
};

// Cached orders
let orderCache = null;

/**
 * Retrieves all orders with caching
 * @returns {Promise<Array>} Array of orders
 */
const getAllOrders = async () => {
  if (navigator.onLine) {
    try {
      const querySnapshot = await getDocs(collection(db, 'orders'));
      const firebaseOrders = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        firebaseId: doc.id
      }));
      orderCache = firebaseOrders;
      return firebaseOrders;
    } catch (error) {
      logger.error('Error fetching Firebase orders:', error);
      return orderCache || [];
    }
  } else {
    try {
      if (orderCache) return orderCache;
      const idb = await initializeDB();
      return await idb.getAll('orders');
    } catch (error) {
      logger.error('Error fetching IndexedDB orders:', error);
      return [];
    }
  }
};

// Auto-sync on online status
window.addEventListener('online', () => syncPendingOrders());

export { saveOrder, getAllOrders, syncPendingOrders };